import { InputRef } from '@unistocks/components/basic/input';
import { DatePickerInputRef } from '@unistocks/components/date-picker/date-picker/types';
import { Form } from '@unistocks/components/form/form';
import {
  annualReturnsTransform,
  savingsGoalSchemaFactory
} from '@unistocks/features/savings-goals/components/forms/schema';
import { SavingsGoalCreateOrUpdateDto } from '@unistocks/proxy/axios-client';
import { t } from '@unistocks/shared/localization/index.ts';
import { today } from '@unistocks/shared/utils/util';
import { FormProps } from '@unistocks/types/form-props';
import { useMemo, useRef } from 'react';

export type EditSavingsGoalFormProps = FormProps<SavingsGoalCreateOrUpdateDto>;

export const EditSavingsGoalForm: React.FC<EditSavingsGoalFormProps> = ({
  initialValues,
  isSubmitting,
  onSubmit
}) => {
  const schema = useMemo(() => savingsGoalSchemaFactory(), []);
  const nameRef = useRef<InputRef>(null);
  const goalValueRef = useRef<InputRef>(null);
  const endDateRef = useRef<DatePickerInputRef>(null);
  const contributionRef = useRef<InputRef>(null);
  const annualReturnsRef = useRef<InputRef>(null);
  const minEndDate = today().toISODate();

  return (
    <Form
      id={'edit-savings-goal'}
      schema={schema}
      initialValues={initialValues}
      isLoading={isSubmitting}
      onSubmit={onSubmit}
    >
      <Form.Input
        ref={nameRef}
        name={'name'}
        label={t('::SavingsGoal:Name')}
        placeholder={t('::SavingsGoal:Name:Placeholder')}
        helpText={t('::SavingsGoal:Name:HelpText')}
        isRequired
        returnKeyType="next"
      />
      <Form.Datepicker
        ref={endDateRef}
        name={'endDate'}
        label={t('::SavingsGoal:EndDate')}
        helpText={t('::SavingsGoal:EndDate:HelpText')}
        minDate={minEndDate}
        isRequired
      />
      <Form.Input
        ref={goalValueRef}
        name={'goalValue'}
        label={t('::SavingsGoal:Amount')}
        helpText={t('::SavingsGoal:Amount:HelpText')}
        appendText={initialValues.currency ?? ''}
        isRequired
        textContentType="none"
        keyboardType="numeric"
        returnKeyType="next"
        onSubmitEditing={() => contributionRef.current?.focus()}
      />
      <Form.Input
        ref={contributionRef}
        name={'contribution'}
        label={t('::SavingsGoal:MonthlyContribution')}
        placeholder={t('::SavingsGoal:Contribution:Amount:Placeholder')}
        helpText={t('::SavingsGoal:Contribution:Amount:HelpText')}
        appendText={initialValues.currency ?? ''}
        textContentType="none"
        keyboardType="numeric"
        returnKeyType="next"
        onSubmitEditing={() => annualReturnsRef.current?.focus()}
      />
      <Form.Input
        ref={annualReturnsRef}
        name={'annualReturns'}
        label={t('::SavingsGoal:AnnualReturns')}
        helpText={t('::SavingsGoal:AnnualReturns:HelpText')}
        appendText="%"
        textContentType="none"
        keyboardType="numeric"
        returnKeyType="done"
        valueTransform={annualReturnsTransform}
      />
    </Form>
  );
};

EditSavingsGoalForm.displayName = 'EditSavingsGoalForm';
